import React, { useEffect, useState, useMemo, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Palette, Save, Loader2, AlertCircle, Image as ImageIcon, Upload, X,
  Bold, Italic, List, ListOrdered, Link as LinkIcon, Strikethrough, Code,
  Eye, Sparkles, Type,
} from "lucide-react";
import { PALETTES, PALETTE_KEYS, renderMarkdown } from "@/lib/landing";
import { FONT_OPTIONS, applyFonts } from "@/lib/landingFonts";
import { LandingView } from "@/pages/public/PublicSchoolLandingPage";
import { compressImage } from "@/lib/imageCompress";

/* ----------------------------- Custom hex picker ----------------------------- */
function CustomHexPicker({ value, onChange }) {
  const isHex = typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value);
  const [draft, setDraft] = useState(isHex ? value : "#2563EB");
  useEffect(() => { if (isHex) setDraft(value); }, [value, isHex]);
  const valid = /^#[0-9a-fA-F]{6}$/.test(draft);
  const apply = () => { if (valid) onChange(draft.toLowerCase()); };
  return (
    <div className={`mt-4 rounded-2xl border-2 p-3 ${isHex ? "border-[#2563EB]" : "border-dashed border-slate-200"}`} data-testid="custom-hex-picker">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-heading font-semibold text-slate-600">Custom color (hex)</span>
        {isHex && <span className="text-[10px] uppercase font-bold tracking-wider text-white rounded-full px-2 py-0.5" style={{ background: `linear-gradient(135deg, ${draft}, ${draft})` }}>Active</span>}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="color"
          value={valid ? draft : "#2563EB"}
          onChange={(e) => setDraft(e.target.value)}
          className="w-10 h-10 rounded-xl border border-slate-200 cursor-pointer p-0.5 bg-white"
          aria-label="Pick a custom theme color"
          data-testid="custom-hex-color-input"
        />
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value.startsWith("#") ? e.target.value : `#${e.target.value}`)}
          onBlur={apply}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
          maxLength={7}
          placeholder="#2563EB"
          className="h-10 rounded-xl border-[#2563EB]/20 font-mono uppercase tracking-wide"
          data-testid="custom-hex-text-input"
        />
        <Button
          type="button"
          onClick={apply}
          disabled={!valid || draft.toLowerCase() === (isHex ? value.toLowerCase() : "")}
          className="rounded-full h-10 px-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-heading font-semibold"
          data-testid="custom-hex-apply"
        >Apply</Button>
      </div>
      {!valid && <p className="mt-1.5 text-[11px] text-rose-500">Enter a valid 6-digit hex (e.g. #2563EB).</p>}
      <p className="mt-1.5 text-[11px] text-slate-500">Picks a single seed colour — we auto-derive the lighter/darker shades used across the page.</p>
    </div>
  );
}

/* ----------------------------- Font picker ----------------------------- */
function FontPicker({ label, value, onChange, testid }) {
  // Preload all curated fonts so each <option> previews in its own family.
  useEffect(() => {
    FONT_OPTIONS.forEach((f) => {
      const id = `sb-font-${f.family.replace(/\s+/g, "-")}`;
      if (document.getElementById(id)) return;
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = f.gfUrl;
      document.head.appendChild(link);
    });
  }, []);
  const previewFamily = value || "";
  // Trigger Google Fonts load on hover/focus so the dropdown preview renders.
  return (
    <div>
      <label className="block text-xs font-heading font-semibold text-slate-600 mb-1">{label}</label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-10 rounded-2xl border border-[#2563EB]/20 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30"
        style={previewFamily ? { fontFamily: `'${previewFamily}', system-ui, sans-serif` } : undefined}
      >
        <option value="">Default (SchoolBoss)</option>
        {FONT_OPTIONS.map((f) => (
          <option key={f.family} value={f.family} style={{ fontFamily: `'${f.family}', system-ui, sans-serif` }}>
            {f.family}
          </option>
        ))}
      </select>
      {previewFamily && (
        <p className="mt-1.5 text-xs text-slate-500" style={{ fontFamily: `'${previewFamily}', system-ui, sans-serif` }}>
          The quick brown fox jumps over the lazy dog.
        </p>
      )}
    </div>
  );
}


/* ----------------------------- Image helper ----------------------------- */
// Re-export under the old local name so existing call sites work without
// editing them — defaults match the user's 400KB app-wide cap.
const fileToCompressedDataUrl = (file, maxDim = 1280, _targetBytes = 400_000) =>
  compressImage(file, { maxKB: 400, maxDim });

/* ----------------------------- Markdown toolbar ----------------------------- */
function MarkdownToolbar({ textareaRef, value, onChange }) {
  const wrap = (before, after = before) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const sel = value.substring(start, end) || "text";
    const next = value.substring(0, start) + before + sel + after + value.substring(end);
    onChange(next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, start + before.length + sel.length);
    }, 0);
  };

  const linePrefix = (prefix) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    onChange(value.substring(0, lineStart) + prefix + value.substring(lineStart));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + prefix.length, start + prefix.length); }, 0);
  };

  const insertLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (!url) return;
    wrap("[", `](${url})`);
  };

  const btn = (icon, action, title) => (
    <button
      type="button" onClick={action} title={title}
      className="w-8 h-8 rounded-lg hover:bg-[#EFF6FF] text-slate-600 hover:text-[#1D4ED8] flex items-center justify-center transition"
    >{icon}</button>
  );

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-[#2563EB]/10 bg-slate-50 rounded-t-2xl">
      {btn(<Bold className="w-4 h-4" />, () => wrap("**"), "Bold")}
      {btn(<Italic className="w-4 h-4" />, () => wrap("*"), "Italic")}
      {btn(<Strikethrough className="w-4 h-4" />, () => wrap("~~"), "Strikethrough")}
      {btn(<Code className="w-4 h-4" />, () => wrap("`"), "Code")}
      <div className="w-px h-5 bg-slate-200 mx-1" />
      {btn(<List className="w-4 h-4" />, () => linePrefix("- "), "Bullet list")}
      {btn(<ListOrdered className="w-4 h-4" />, () => linePrefix("1. "), "Numbered list")}
      {btn(<LinkIcon className="w-4 h-4" />, insertLink, "Link")}
    </div>
  );
}

/* ----------------------------- Main page ----------------------------- */
export default function LandingSettingsPage() {
  const [schools, setSchools] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    theme_color: "blue",
    public_display_name: "",
    public_description_md: "",
    hero_photo_base64: null,
  });
  const heroInputRef = useRef(null);
  const descRef = useRef(null);
  const [branches, setBranches] = useState([]);

  // Load approved schools
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/schools/mine");
        const approved = (data || []).filter((s) => s.status === "approved" && s.slug);
        setSchools(approved);
        if (approved.length > 0 && !selectedId) setSelectedId(approved[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When selectedId changes, hydrate the form + load branches via public endpoint
  useEffect(() => {
    if (!selectedId) return;
    const s = schools.find((x) => x.id === selectedId);
    if (!s) return;
    setForm({
      theme_color: s.theme_color || "blue",
      public_display_name: s.public_display_name || s.name || "",
      public_description_md: s.public_description_md || s.description || "",
      hero_photo_base64: s.hero_photo_base64 || null,
      font_heading: s.font_heading || "",
      font_body: s.font_body || "",
      font_button: s.font_button || "",
      payment_details_text: s.payment_details_text || "",
      payment_account_name: s.payment_account_name || "",
      print_tagline: s.print_tagline || "",
    });
    // load branches for live preview
    if (s.slug) {
      api.get(`/public/schools/${s.slug}`)
        .then(({ data }) => setBranches(data.branches || []))
        .catch(() => setBranches([]));
    }
  }, [selectedId, schools]);

  const selectedSchool = useMemo(() => schools.find((s) => s.id === selectedId), [schools, selectedId]);

  const previewSchool = useMemo(() => ({
    ...(selectedSchool || {}),
    theme_color: form.theme_color,
    public_display_name: form.public_display_name,
    public_description_md: form.public_description_md,
    hero_photo_base64: form.hero_photo_base64,
  }), [selectedSchool, form]);

  const onHeroFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    try {
      const compressed = await fileToCompressedDataUrl(file);
      setForm((f) => ({ ...f, hero_photo_base64: compressed }));
    } catch { toast.error("Could not process image"); }
  };

  const removeHero = () => setForm((f) => ({ ...f, hero_photo_base64: null }));

  const save = async () => {
    if (!selectedId) return;
    if (!form.public_display_name?.trim()) {
      toast.error("Public display name cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        theme_color: form.theme_color,
        public_display_name: form.public_display_name.trim(),
        public_description_md: form.public_description_md,
        // Empty string means "remove"; nothing means "no change"
        hero_photo_base64: form.hero_photo_base64 === null ? "" : form.hero_photo_base64,
        font_heading: form.font_heading || "default",
        font_body: form.font_body || "default",
        font_button: form.font_button || "default",
        payment_details_text: form.payment_details_text || "",
        payment_account_name: form.payment_account_name || "",
        print_tagline: form.print_tagline || "",
      };
      const { data } = await api.patch(`/schools/${selectedId}`, payload);
      toast.success("Landing page updated");
      setSchools((prev) => prev.map((s) => s.id === selectedId ? { ...s, ...data } : s));
      // Re-apply fonts immediately so the live UI reflects the change.
      applyFonts({
        heading: form.font_heading || null,
        body: form.font_body || null,
        button: form.font_button || null,
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" /></div></>;
  }

  if (schools.length === 0) {
    return (
      <>
        <div className="bg-white rounded-3xl shadow-floating p-10 max-w-xl mx-auto text-center" data-testid="no-approved-school">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
            <AlertCircle className="w-7 h-7 text-[#E65100]" />
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">No approved school yet</h2>
          <p className="text-slate-500 mt-2">Landing page customization unlocks once your school is approved by SchoolBoss.</p>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#DBEAFE] to-[#BFDBFE] flex items-center justify-center">
            <Palette className="w-6 h-6 text-[#2563EB]" />
          </div>
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight">Landing Page</h1>
            <p className="text-slate-500 mt-1 text-sm">Customize how your school looks to the public.</p>
          </div>
        </div>
        <div className="flex gap-2">
          {selectedSchool?.slug && (
            <a href={`/school/${selectedSchool.slug}`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-full border-[#2563EB]/30 text-[#1D4ED8] hover:bg-[#EFF6FF] h-11" data-testid="open-public-page">
                <Eye className="w-4 h-4 mr-1.5" /> View live
              </Button>
            </a>
          )}
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-full h-11 px-6 bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white shadow-floating font-heading font-semibold"
            data-testid="landing-save-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving</> : <><Save className="w-4 h-4 mr-1.5" /> Save changes</>}
          </Button>
        </div>
      </div>

      {/* School picker (if more than 1) */}
      {schools.length > 1 && (
        <div className="mb-5 flex flex-wrap gap-2">
          {schools.map((s) => (
            <button
              key={s.id}
              onClick={() => setSelectedId(s.id)}
              className={`px-4 py-2 rounded-full font-heading text-sm font-semibold transition ${
                selectedId === s.id ? "bg-[#2563EB] text-white shadow-sm" : "bg-white text-foreground border border-[#2563EB]/20 hover:bg-[#EFF6FF]"
              }`}
              data-testid={`pick-school-${s.id}`}
            >{s.name}</button>
          ))}
        </div>
      )}

      {/* Editor + Preview */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* LEFT — Editor */}
        <div className="space-y-5">
          {/* Theme color */}
          <section className="bg-white rounded-3xl shadow-floating p-5" data-testid="theme-color-card">
            <h3 className="font-heading text-base font-bold mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#2563EB]" /> Theme color
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-2.5">
              {PALETTE_KEYS.map((k) => {
                const p = PALETTES[k];
                const active = form.theme_color === k;
                return (
                  <button
                    key={k} type="button"
                    onClick={() => setForm((f) => ({ ...f, theme_color: k }))}
                    className={`relative rounded-2xl p-2 border-2 transition ${active ? "border-[#2563EB] shadow-md" : "border-transparent hover:border-[#2563EB]/30"}`}
                    data-testid={`theme-${k}`}
                  >
                    <div className="h-10 rounded-xl mb-1.5" style={{ background: `linear-gradient(135deg, ${p.from}, ${p.to})` }} />
                    <div className="text-[10px] font-heading font-semibold text-foreground">{p.label}</div>
                  </button>
                );
              })}
            </div>
            <CustomHexPicker
              value={form.theme_color}
              onChange={(v) => setForm((f) => ({ ...f, theme_color: v }))}
            />
          </section>

          {/* Typography */}
          <section className="bg-white rounded-3xl shadow-floating p-5" data-testid="typography-card">
            <h3 className="font-heading text-base font-bold mb-3 flex items-center gap-2">
              <Type className="w-4 h-4 text-[#2563EB]" /> Typography
            </h3>
            <p className="text-[11px] text-slate-500 -mt-2 mb-3">
              Choose Google Fonts for headings, body text, and buttons. Leave on <strong>Default</strong> to keep the SchoolBoss style. Applies to your landing page, parent portal, and the entire school dashboard.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <FontPicker
                label="Headings"
                testid="font-heading-select"
                value={form.font_heading}
                onChange={(v) => setForm((f) => ({ ...f, font_heading: v }))}
              />
              <FontPicker
                label="Body text"
                testid="font-body-select"
                value={form.font_body}
                onChange={(v) => setForm((f) => ({ ...f, font_body: v }))}
              />
              <FontPicker
                label="Buttons"
                testid="font-button-select"
                value={form.font_button}
                onChange={(v) => setForm((f) => ({ ...f, font_button: v }))}
              />
            </div>
          </section>

          {/* Public display name */}
          <section className="bg-white rounded-3xl shadow-floating p-5">
            <h3 className="font-heading text-base font-bold mb-2">Public display name</h3>
            <Input
              value={form.public_display_name}
              onChange={(e) => setForm((f) => ({ ...f, public_display_name: e.target.value }))}
              className="h-11 rounded-2xl border-[#2563EB]/20"
              maxLength={80}
              placeholder="e.g. Little Shepherd Therapy"
              data-testid="display-name-input"
            />
            <p className="text-[11px] text-slate-500 mt-1.5">Shown as the big headline on your public page. Internal/admin record name stays unchanged.</p>
          </section>

          {/* Hero photo */}
          <section className="bg-white rounded-3xl shadow-floating p-5" data-testid="hero-photo-card">
            <h3 className="font-heading text-base font-bold mb-2">Hero banner photo</h3>
            <div className="rounded-2xl border-2 border-dashed border-[#2563EB]/25 overflow-hidden bg-slate-50">
              {form.hero_photo_base64 ? (
                <div className="relative h-44">
                  <img src={form.hero_photo_base64} alt="hero" className="w-full h-full object-cover" data-testid="hero-photo-preview" />
                  <button
                    onClick={removeHero}
                    type="button"
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white shadow-md text-[#C62828] hover:bg-red-50 flex items-center justify-center"
                    data-testid="hero-photo-remove"
                  ><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <button
                  type="button" onClick={() => heroInputRef.current?.click()}
                  className="w-full h-44 flex flex-col items-center justify-center text-slate-500 hover:bg-[#EFF6FF]/40 transition"
                  data-testid="hero-photo-upload"
                >
                  <Upload className="w-7 h-7 mb-1.5" />
                  <p className="font-heading font-semibold">Upload banner photo</p>
                  <p className="text-xs text-slate-400 mt-0.5">Recommended: 1600×600. Auto-compressed for you.</p>
                </button>
              )}
            </div>
            <input ref={heroInputRef} type="file" accept="image/*" className="hidden" onChange={onHeroFile} />
          </section>

          {/* Description (markdown) */}
          <section className="bg-white rounded-3xl shadow-floating p-5" data-testid="description-card">
            <h3 className="font-heading text-base font-bold mb-2">Description</h3>
            <div className="rounded-2xl border border-[#2563EB]/15 overflow-hidden">
              <MarkdownToolbar
                textareaRef={descRef}
                value={form.public_description_md}
                onChange={(v) => setForm((f) => ({ ...f, public_description_md: v }))}
              />
              <Textarea
                ref={descRef}
                value={form.public_description_md}
                onChange={(e) => setForm((f) => ({ ...f, public_description_md: e.target.value }))}
                placeholder={"Tell parents about your school.\n\nSupports **bold**, *italic*, `code`, [links](https://…) and lists."}
                className="border-0 rounded-none min-h-[180px] focus-visible:ring-0 focus-visible:ring-offset-0 resize-y"
                maxLength={4000}
                data-testid="description-input"
              />
            </div>
            <div className="flex items-center justify-between text-[11px] text-slate-500 mt-1.5">
              <span>Markdown supported — **bold**, *italic*, lists, [links](https://…), ~~strike~~, `code`</span>
              <span>{form.public_description_md.length}/4000</span>
            </div>
          </section>

          {/* Statement of Account — school-wide print settings */}
          <section className="bg-white rounded-3xl shadow-floating p-5" data-testid="soa-settings-card">
            <h3 className="font-heading text-base font-bold mb-1">Statement of Account (Print)</h3>
            <p className="text-[11px] text-slate-500 mb-3">Shown on bulk-printed Statements of Account for this school.</p>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-heading font-bold uppercase tracking-wider text-slate-500 block mb-1">Print Tagline</label>
                <input
                  value={form.print_tagline || ""}
                  onChange={(e) => setForm((f) => ({ ...f, print_tagline: e.target.value }))}
                  placeholder='e.g. "A Progressive Approach Playschool"'
                  maxLength={200}
                  className="w-full h-11 rounded-2xl border border-[#2563EB]/15 px-3 text-sm"
                  data-testid="soa-print-tagline"
                />
              </div>
              <div>
                <label className="text-[11px] font-heading font-bold uppercase tracking-wider text-slate-500 block mb-1">Account Name (owner)</label>
                <input
                  value={form.payment_account_name || ""}
                  onChange={(e) => setForm((f) => ({ ...f, payment_account_name: e.target.value }))}
                  placeholder="e.g. Mylene E. Malate"
                  maxLength={120}
                  className="w-full h-11 rounded-2xl border border-[#2563EB]/15 px-3 text-sm"
                  data-testid="soa-account-name"
                />
                <p className="text-[10.5px] text-slate-500 mt-1">Printed above the bank list on the Statement of Account.</p>
              </div>
              <div>
                <label className="text-[11px] font-heading font-bold uppercase tracking-wider text-slate-500 block mb-1">Payment / Bank Details</label>
                <Textarea
                  value={form.payment_details_text || ""}
                  onChange={(e) => setForm((f) => ({ ...f, payment_details_text: e.target.value }))}
                  placeholder={"BDO - 011108001607 (Mylene E. Malate)\nGCash - 09179832931 (Mylene E. Malate)\nPayMaya - 09179832931 (Mylene E. Malate)"}
                  maxLength={2000}
                  rows={4}
                  className="rounded-2xl border border-[#2563EB]/15 font-mono text-sm"
                  data-testid="soa-payment-details"
                />
                <p className="text-[10.5px] text-slate-500 mt-1">One line per account. Rendered as-is at the SOA footer.</p>
              </div>
            </div>
          </section>
        </div>

        {/* RIGHT — Live preview */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          <div className="bg-white rounded-3xl shadow-floating overflow-hidden border border-[#2563EB]/10" data-testid="live-preview">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#2563EB]/10 bg-slate-50">
              <div className="flex items-center gap-2 text-xs font-heading font-semibold text-slate-600">
                <Eye className="w-3.5 h-3.5" /> Live preview
              </div>
              <span className="text-[10px] text-slate-400 truncate max-w-[60%]">/school/{selectedSchool?.slug}</span>
            </div>
            <div className="h-[calc(100vh-260px)] min-h-[520px] overflow-y-auto bg-white">
              <div className="scale-[0.62] origin-top-left w-[161%]">
                <LandingView school={previewSchool} branches={branches} compact />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
